<script setup lang="ts">
import { ref } from 'vue'
import type { ClientRoom } from '../types'

const props = defineProps<{
    currentRoom: ClientRoom | null
}>()

const emit = defineEmits<{
    'set-object': [objectIndex: number, property: string, value: number | string]
    'set-room': [property: string, value: number | string]
    'toggle-object-visibility': [objectIndex: number]
}>()

const fields: { label: string; prop: string; step: number }[] = [
    { label: 'x',     prop: 'x',        step: 1   },
    { label: 'y',     prop: 'y',        step: 1   },
    { label: 'ox',    prop: 'offset.x', step: 1   },
    { label: 'oy',    prop: 'offset.y', step: 1   },
    { label: 'scale', prop: 'scale',    step: 0.1 },
]

const copyStatus = ref<'idle' | 'copied'>('idle')

function onObjectValueChange(objectIndex: number, property: string, event: Event)
{
    const raw = (event.target as HTMLInputElement).value
    const value = parseFloat(raw)
    emit('set-object', objectIndex, property, isNaN(value) ? 0 : value)
}

function onObjectUrlChange(objectIndex: number, event: Event)
{
    const value = (event.target as HTMLInputElement).value
    emit('set-object', objectIndex, 'url', value)
}

function getObjectUrlValue(obj: NonNullable<ClientRoom>['objects'][number]): string
{
    return Array.isArray(obj.url) ? obj.url.join(', ') : obj.url
}

function getObjectValue(obj: NonNullable<ClientRoom>['objects'][number], property: string): number
{
    if (property === 'x') return obj.x
    if (property === 'y') return obj.y
    if (property === 'offset.x') return obj.offset?.x ?? 0
    if (property === 'offset.y') return obj.offset?.y ?? 0
    if (property === 'scale') return obj.scale ?? 1
    return 0
}

function getRoomValue(property: string): number
{
    if (!props.currentRoom) return 0
    if (property === 'scale') return props.currentRoom.scale
    if (property === 'originCoordinates.x') return props.currentRoom.originCoordinates.x
    if (property === 'originCoordinates.y') return props.currentRoom.originCoordinates.y
    return 0
}

function onRoomValueChange(property: string, event: Event)
{
    const raw = (event.target as HTMLInputElement).value
    const value = parseFloat(raw)
    emit('set-room', property, isNaN(value) ? 0 : value)
}

function onRoomTextValueChange(property: string, event: Event)
{
    const value = (event.target as HTMLInputElement).value
    emit('set-room', property, value)
}

function getRoomTextValue(property: string): string
{
    if (!props.currentRoom) return ''
    if (property === 'backgroundImageUrl') return props.currentRoom.backgroundImageUrl
    return ''
}

function formatObjectsJson(objects: Record<string, unknown>[]): string
{
    return [
        '[',
        ...objects.map((object, index) => `    ${JSON.stringify(object)},`),
        ']',
    ].join('\n')
}

function copyObjectsJson()
{
    if (!props.currentRoom) return
    const stripped = props.currentRoom.objects.map(o => {
        const out: Record<string, unknown> = { x: o.x, y: o.y }
        if (o.id !== undefined)     out.id = o.id
        if (o.width !== undefined)  out.width = o.width
        if (o.height !== undefined) out.height = o.height
        out.url = o.url
        if (o.animation !== undefined) out.animation = o.animation
        if (o.scale !== undefined)  out.scale = o.scale
        if (o.offset !== undefined) out.offset = o.offset
        return out
    })
    const json = formatObjectsJson(stripped)
    navigator.clipboard.writeText(json).then(() => {
        copyStatus.value = 'copied'
        setTimeout(() => { copyStatus.value = 'idle' }, 2000)
    })
}

function toggleVisibility(objectIndex: number)
{
    emit('toggle-object-visibility', objectIndex)
}
</script>

<template>
    <div id="room-object-editor" v-if="currentRoom">
        <div>
            <label>scale</label>
            <input
                type="number"
                :step="0.01"
                :value="getRoomValue('scale')"
                @change="onRoomValueChange('scale', $event)">
            <label>backgroundImageUrl</label>
            <input
                type="text"
                class="room-object-editor-background-url-input"
                :value="getRoomTextValue('backgroundImageUrl')"
                @change="onRoomTextValueChange('backgroundImageUrl', $event)">
            <label>originCoordinates.x</label>
            <input
                type="number"
                :step="1"
                :value="getRoomValue('originCoordinates.x')"
                @change="onRoomValueChange('originCoordinates.x', $event)">
            <label>originCoordinates.y</label>
            <input
                type="number"
                :step="1"
                :value="getRoomValue('originCoordinates.y')"
                @change="onRoomValueChange('originCoordinates.y', $event)">
        </div>
        <div id="room-object-editor-toolbar">
            <button @click="copyObjectsJson">{{ copyStatus === 'copied' ? '✓ Copied!' : 'Copy objects JSON' }}</button>
        </div>
        <div
            v-for="(obj, index) in currentRoom.objects"
            :key="index"
            class="room-object-editor-row"
        >
            <span class="room-object-editor-label">{{ obj.url || ('[' + index + ']') }}</span>
            <span class="room-object-editor-field room-object-editor-url-field">
                <label class="room-object-editor-field-name">url</label>
                <input
                    class="room-object-editor-input room-object-editor-url-input"
                    type="text"
                    :value="getObjectUrlValue(obj)"
                    @change="onObjectUrlChange(index, $event)"
                />
            </span>
            <span class="room-object-editor-field">
                <button @click="toggleVisibility(index)">{{ obj.isHidden ? '無' : '有' }}</button>
            </span>
            <span v-for="field in fields"
                  :key="field.prop"
                  class="room-object-editor-field">
                <label class="room-object-editor-field-name">{{ field.label }}</label>
                <input
                    class="room-object-editor-input"
                    type="number"
                    :step="field.step"
                    :value="getObjectValue(obj, field.prop)"
                    @change="onObjectValueChange(index, field.prop, $event)"
                />
            </span>
        </div>
    </div>
</template>

<style>
#room-object-editor {
    padding: 4px;
    font-size: 11px;
    border-top: 1px solid #aaa;
    margin-top: 4px;
}

#room-object-editor-toolbar {
    margin-bottom: 4px;
}

.room-object-editor-row {
    display: grid;
    grid-template-columns: 140px repeat(7, auto);
    align-items: center;
    gap: 4px;
    padding: 2px 0;
    border-bottom: 1px dotted #ccc;
}

.room-object-editor-label {
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.room-object-editor-field {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.room-object-editor-field-name {
    font-size: 10px;
}

.room-object-editor-input {
    width: 52px;
    font-size: 11px;
    padding: 0 2px;
}

.room-object-editor-url-field {
    min-width: 220px;
}

.room-object-editor-url-input {
    width: 200px;
}

.room-object-editor-row button {
    padding: 0 4px;
    line-height: 1.2;
    font-size: 12px;
    cursor: pointer;
}

.room-object-editor-background-url-input {
    width: 240px;
    font-size: 11px;
    padding: 0 2px;
}
</style>
